import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { InvoiceForm } from "@/features/invoices/components/invoice-form";
import { InvoiceHistory } from "@/features/invoices/components/invoice-history";
import { 
  useInvoice, 
  useInvoiceHistory, 
  downloadInvoicePDF, 
  useUpdateInvoiceStatus,
  useUpdateInvoiceDates,
  useDeleteInvoice,
  useCancelInvoice,
  useCopyInvoice,
} from "@/features/invoices/hooks/use-invoices";
import config from "@/lib/config";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { type InvoiceStatus } from "@/features/invoices/types/invoice.types";
import { useDateFormat } from "@/hooks/use-date-format";
import { Calendar, FileText, Download, Send, CheckCircle, Trash2, XCircle, Copy, Eye } from "lucide-react";
import { useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { useQueryClient } from "@tanstack/react-query";

export const Route = createFileRoute("/(app)/dashboard/invoices/edit/$id")({
  component: EditInvoicePage,
});

function EditInvoicePage() {
  const { t } = useTranslation();
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { formatDate } = useDateFormat();
  const { data: invoice, isLoading, refetch: refetchInvoice } = useInvoice(id);
  const { data: history, refetch: refetchHistory } = useInvoiceHistory(id);
  const [isDownloading, setIsDownloading] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [showPdfPreview, setShowPdfPreview] = useState(false);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [cancellationReason, setCancellationReason] = useState("");
  const [editingSentAt, setEditingSentAt] = useState(false);
  const [editingPaidAt, setEditingPaidAt] = useState(false);
  const updateStatus = useUpdateInvoiceStatus();
  const updateDates = useUpdateInvoiceDates();
  const deleteInvoice = useDeleteInvoice();
  const cancelInvoice = useCancelInvoice();
  const copyInvoice = useCopyInvoice();

  const handleSuccess = () => {
    navigate({ to: "/dashboard/invoices" });
  };

  if (isLoading) {
    return (
      <div className="w-full max-w-full space-y-4 md:space-y-6 overflow-hidden">
        <p>{t('invoices.editPage.loadingInvoice')}</p>
      </div>
    );
  }

  if (!invoice) {
    return (
      <div className="w-full max-w-full space-y-4 md:space-y-6 overflow-hidden">
        <p>{t('invoices.editPage.invoiceNotFound')}</p>
      </div>
    );
  }

  const canEdit = invoice.status === "draft";

  const getStatusBadgeVariant = (status: InvoiceStatus): "default" | "secondary" | "destructive" => {
    switch (status) {
      case "draft":
        return "secondary";
      case "sent":
        return "destructive";
      case "paid":
        return "default";
      default:
        return "default";
    }
  };

  const getStatusBadgeClassName = (status: InvoiceStatus): string => {
    if (status === "paid") {
      return "bg-green-100 text-green-800 hover:bg-green-100 border-green-200";
    }
    return "";
  };

  // Get status-specific date from history
  const getStatusDate = () => {
    const createdDate = formatDate(new Date(invoice.createdAt));
    
    if (!history || history.length === 0) {
      return { label: t('invoices.editPage.createdOn'), date: createdDate };
    }

    // Find the most recent status change
    if (invoice.status === "paid") {
      const paidEntry = history.find(entry => 
        entry.action === "status_changed" && entry.newStatus === "paid"
      );
      if (paidEntry) {
        return { label: t('invoices.editPage.paidOn'), date: formatDate(new Date(paidEntry.createdAt)) };
      }
    }

    if (invoice.status === "sent") {
      const sentEntry = history.find(entry => 
        entry.action === "status_changed" && entry.newStatus === "sent"
      );
      if (sentEntry) {
        return { label: t('invoices.editPage.sentOn'), date: formatDate(new Date(sentEntry.createdAt)) };
      }
    }

    return { label: t('invoices.editPage.createdOn'), date: createdDate };
  };

  const statusDate = getStatusDate();

  const handleDownloadPDF = async () => {
    setIsDownloading(true);
    try {
      await downloadInvoicePDF(id, invoice.invoiceNumber);
      toast({
        title: t('invoices.editPage.pdfDownloaded'),
        description: t('invoices.editPage.pdfDownloadedDescription'),
      });
    } catch (error) {
      console.error("PDF download failed:", error);
      toast({
        title: t('common.error'),
        description: t('invoices.editPage.pdfError'),
        variant: "destructive",
      });
    } finally {
      setIsDownloading(false);
    }
  };

  const handlePreviewPDF = async () => {
    setIsDownloading(true);
    try {
      const token = localStorage.getItem("auth-data");
      const headers: Record<string, string> = {};

      if (token) {
        headers["Authorization"] = `Bearer ${token}`;
      }

      const response = await fetch(`${config.apiUrl}/api/invoices/${id}/pdf`, {
        credentials: "include",
        headers,
      });

      if (!response.ok) {
        throw new Error("PDF konnte nicht geladen werden");
      }

      // Create blob URL for preview
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      setPdfUrl(url);
      setShowPdfPreview(true);
    } catch (error) {
      console.error("PDF preview failed:", error);
      toast({
        title: t('common.error'),
        description: t('invoices.editPage.pdfPreviewError'),
        variant: "destructive",
      });
    } finally {
      setIsDownloading(false);
    }
  };

  const handleClosePdfPreview = () => {
    setShowPdfPreview(false);
    if (pdfUrl) {
      window.URL.revokeObjectURL(pdfUrl);
      setPdfUrl(null);
    }
  };

  const handleMarkAsSent = async () => {
    if (!invoice?.id) return;
    try {
      await updateStatus.mutateAsync({
        id: invoice.id,
        status: "sent",
      });
      toast({
        title: t('invoices.editPage.statusUpdated'),
        description: t('invoices.editPage.markedAsSent'),
      });
    } catch (error) {
      console.error("Status update failed:", error);
      toast({
        title: t('common.error'),
        description: t('invoices.editPage.statusUpdateError'),
        variant: "destructive",
      });
    }
  };

  const handleMarkAsPaid = async () => {
    if (!invoice?.id) return;
    try {
      await updateStatus.mutateAsync({
        id: invoice.id,
        status: "paid",
      });
      toast({
        title: t('invoices.editPage.statusUpdated'),
        description: t('invoices.editPage.markedAsPaid'),
      });
    } catch (error) {
      console.error("Status update failed:", error);
      toast({
        title: t('common.error'),
        description: t('invoices.editPage.statusUpdateError'),
        variant: "destructive",
      });
    }
  };

  const handleMarkAsUnpaid = async () => {
    if (!invoice?.id) return;
    try {
      await updateStatus.mutateAsync({
        id: invoice.id,
        status: "sent",
      });
      toast({
        title: t('invoices.editPage.statusUpdated'),
        description: t('invoices.editPage.markedAsUnpaid'),
      });
    } catch (error) {
      console.error("Status update failed:", error);
      toast({
        title: t('common.error'),
        description: t('invoices.editPage.statusUpdateError'),
        variant: "destructive",
      });
    }
  };

  const handleDelete = async () => {
    setShowDeleteDialog(false);
    try {
      await deleteInvoice.mutateAsync(id);
      toast({
        title: t('invoices.editPage.invoiceDeleted'),
        description: t('invoices.editPage.invoiceDeletedDescription'),
      });
      // Use replace to prevent going back to deleted invoice
      navigate({ to: "/dashboard/invoices", replace: true });
    } catch (error) {
      console.error("Delete failed:", error);
      toast({
        title: t('common.error'),
        description: t('invoices.editPage.invoiceDeleteError'),
        variant: "destructive",
      });
    }
  };

  const handleCancel = async () => {
    if (!cancellationReason.trim()) {
      toast({
        title: t('common.error'),
        description: t('invoices.editPage.cancellationReasonRequired'),
        variant: "destructive",
      });
      return;
    }

    try {
      await cancelInvoice.mutateAsync({
        id,
        reason: cancellationReason,
      });
      toast({
        title: t('invoices.editPage.invoiceCancelled'),
        description: t('invoices.editPage.invoiceCancelledDescription'),
      });
      setShowCancelDialog(false);
      setCancellationReason("");
    } catch (error) {
      console.error("Cancel failed:", error);
      toast({
        title: t('common.error'),
        description: t('invoices.editPage.invoiceCancelError'),
        variant: "destructive",
      });
    }
  };

  const handleUpdateDate = async (field: 'sentAt' | 'paidAt', value: string | null) => {
    try {
      // Close edit mode first
      if (field === 'sentAt') setEditingSentAt(false);
      if (field === 'paidAt') setEditingPaidAt(false);
      
      // Only send the field that is being updated
      const payload: { id: string; sentAt?: string | null; paidAt?: string | null } = { id };
      if (field === 'sentAt') {
        payload.sentAt = value;
      } else {
        payload.paidAt = value;
      }
      
      await updateDates.mutateAsync(payload);
      
      // Force immediate refetch without waiting
      refetchHistory();
      refetchInvoice();
      
      toast({
        title: t('invoices.editPage.dateUpdated'),
        description: t('invoices.editPage.dateUpdatedDescription'),
      });
    } catch (error) {
      console.error("Date update failed:", error);
      toast({
        title: t('common.error'),
        description: t('invoices.editPage.dateUpdateError'),
        variant: "destructive",
      });
    }
  };

  const handleCopy = async () => {
    try {
      const newInvoice = await copyInvoice.mutateAsync(id);
      toast({
        title: t('invoices.editPage.invoiceCopied'),
        description: t('invoices.editPage.invoiceCopiedDescription'),
      });
      navigate({ to: `/dashboard/invoices/edit/${newInvoice.id}` });
    } catch (error) {
      console.error("Copy failed:", error);
      toast({
        title: t('common.error'),
        description: t('invoices.editPage.invoiceCopyError'),
        variant: "destructive",
      });
    }
  };

  // Status Info Card Component
  const statusInfoCard = (
    <Card className="h-full">
      <CardContent className="pt-6 pb-6 h-full flex flex-col justify-start gap-4">
        <div>
          <p className="text-sm text-muted-foreground">{t('invoices.status')}</p>
          <Badge 
            variant={getStatusBadgeVariant(invoice.status as InvoiceStatus)}
            className={`mt-1 ${getStatusBadgeClassName(invoice.status as InvoiceStatus)}`}
          >
            {t(`invoices.statusLabels.${invoice.status}`)}
          </Badge>
        </div>
        <div>
          <p className="text-sm text-muted-foreground">{t('invoices.editPage.createdOn')}</p>
          <p className="text-sm font-medium mt-1">{formatDate(new Date(invoice.createdAt))}</p>
        </div>
        
        {/* Editable Sent At Date */}
        {(invoice.status === "sent" || invoice.status === "paid") && (
          <div>
            <p className="text-sm text-muted-foreground flex items-center gap-1 mb-1">
              <Send className="h-3 w-3" />
              {t('invoices.editPage.sentOn')}
            </p>
            {editingSentAt ? (
              <div className="flex gap-1">
                <Input
                  type="date"
                  defaultValue={invoice.sentAt ? new Date(invoice.sentAt).toISOString().split('T')[0] : ''}
                  className="h-7 text-sm"
                  onChange={(e) => {
                    if (e.target.value) {
                      handleUpdateDate('sentAt', e.target.value);
                    } else {
                      setEditingSentAt(false);
                    }
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.currentTarget.blur();
                    }
                    if (e.key === 'Escape') {
                      setEditingSentAt(false);
                    }
                  }}
                  autoFocus
                />
              </div>
            ) : (
              <p 
                className="text-sm font-medium mt-1 cursor-pointer hover:text-primary"
                onClick={() => setEditingSentAt(true)}
              >
                {invoice.sentAt ? formatDate(new Date(invoice.sentAt)) : '-'}
              </p>
            )}
          </div>
        )}
        
        {/* Editable Paid At Date */}
        {invoice.status === "paid" && (
          <div>
            <p className="text-sm text-muted-foreground flex items-center gap-1 mb-1">
              <CheckCircle className="h-3 w-3" />
              {t('invoices.editPage.paidOn')}
            </p>
            {editingPaidAt ? (
              <div className="flex gap-1">
                <Input
                  type="date"
                  defaultValue={invoice.paidAt ? new Date(invoice.paidAt).toISOString().split('T')[0] : ''}
                  className="h-7 text-sm"
                  onChange={(e) => {
                    if (e.target.value) {
                      handleUpdateDate('paidAt', e.target.value);
                    } else {
                      setEditingPaidAt(false);
                    }
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.currentTarget.blur();
                    }
                    if (e.key === 'Escape') {
                      setEditingPaidAt(false);
                    }
                  }}
                  autoFocus
                />
              </div>
            ) : (
              <p 
                className="text-sm font-medium mt-1 cursor-pointer hover:text-primary"
                onClick={() => setEditingPaidAt(true)}
              >
                {invoice.paidAt ? formatDate(new Date(invoice.paidAt)) : '-'}
              </p>
            )}
          </div>
        )}
        
        <div className="pt-2 border-t space-y-3">
          {/* Status-Aktionen */}
          {(invoice.status === "draft" || invoice.status === "sent" || invoice.status === "paid") && (
            <div className="space-y-2">
              {invoice.status === "draft" && (
                <>
                  <Button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      handleMarkAsSent();
                    }}
                    disabled={updateStatus.isPending}
                    variant="default"
                    size="sm"
                    className="w-full"
                  >
                    <Send className="h-4 w-4 mr-2" />
                    {t('invoices.markAsSent')}
                  </Button>
                  <Button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      handleMarkAsPaid();
                    }}
                    disabled={updateStatus.isPending}
                    variant="outline"
                    size="sm"
                    className="w-full"
                  >
                    <CheckCircle className="h-4 w-4 mr-2" />
                    {t('invoices.markAsPaid')}
                  </Button>
                </>
              )}
              {invoice.status === "sent" && (
                <Button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    handleMarkAsPaid();
                  }}
                  disabled={updateStatus.isPending}
                  variant="default"
                  size="sm"
                  className="w-full bg-green-600 hover:bg-green-700"
                >
                  <CheckCircle className="h-4 w-4 mr-2" />
                  {t('invoices.markAsPaid')}
                </Button>
              )}
              {invoice.status === "paid" && (
                <Button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    handleMarkAsUnpaid();
                  }}
                  disabled={updateStatus.isPending}
                  variant="outline"
                  size="sm"
                  className="w-full"
                >
                  <XCircle className="h-4 w-4 mr-2" />
                  {t('invoices.editPage.markAsNotPaid')}
                </Button>
              )}
            </div>
          )}

          {/* PDF-Aktionen */}
          <div className="space-y-2 pt-3 border-t">
            <p className="text-xs font-medium text-muted-foreground mb-2">PDF</p>
            <Button
              onClick={handlePreviewPDF}
              disabled={isDownloading}
              variant="outline"
              size="sm"
              className="w-full"
            >
              <Eye className="h-4 w-4 mr-2" />
              {isDownloading ? t('invoices.editPage.loading') : t('invoices.editPage.preview')}
            </Button>
            <Button
              onClick={handleDownloadPDF}
              disabled={isDownloading}
              variant="outline"
              size="sm"
              className="w-full"
            >
              <Download className="h-4 w-4 mr-2" />
              {isDownloading ? t('invoices.editPage.loading') : t('invoices.editPage.download')}
            </Button>
          </div>

          {/* Verwaltungs-Aktionen */}
          <div className="space-y-2 pt-3 border-t">
            <p className="text-xs font-medium text-muted-foreground mb-2">{t('invoices.editPage.management')}</p>
            <Button
              onClick={handleCopy}
              disabled={copyInvoice.isPending}
              variant="outline"
              size="sm"
              className="w-full"
            >
              <Copy className="h-4 w-4 mr-2" />
              {t('invoices.editPage.copy')}
            </Button>

            {(invoice.status === "sent" || invoice.status === "paid") && (
              <Button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setShowCancelDialog(true);
                }}
                disabled={cancelInvoice.isPending}
                variant="outline"
                size="sm"
                className="w-full text-orange-600 hover:text-orange-700 hover:border-orange-600"
              >
                <XCircle className="h-4 w-4 mr-2" />
                {t('invoices.editPage.cancel')}
              </Button>
            )}

            {invoice.status !== "cancelled" && invoice.status !== "deleted" && (
              <Button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setShowDeleteDialog(true);
                }}
                disabled={deleteInvoice.isPending}
                variant="outline"
                size="sm"
                className="w-full text-red-600 hover:text-red-700 hover:border-red-600"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                {t('invoices.delete')}
              </Button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="w-full max-w-full space-y-4 md:space-y-6 overflow-hidden">
      {/* Header: Invoice Number */}
      <div className="flex items-center gap-3 md:gap-4">
        <FileText className="h-8 w-8 md:h-10 md:w-10 text-primary" />
        <div>
          <p className="text-xs md:text-sm text-muted-foreground">{t('invoices.title')}</p>
          <h1 className="text-xl md:text-3xl font-bold">{invoice.invoiceNumber}</h1>
        </div>
      </div>
      
      {/* Invoice Form with Status Info Sidebar */}
      <InvoiceForm 
        initialData={invoice} 
        onSuccess={handleSuccess} 
        isReadOnly={!canEdit}
        sidebarContent={statusInfoCard}
      />
      
      <InvoiceHistory invoiceId={id} />

      {/* PDF Preview Dialog */}
      <Dialog open={showPdfPreview} onOpenChange={handleClosePdfPreview}>
        <DialogContent className="!max-w-[90vw] w-[90vw] h-[95vh] p-0 gap-0 flex flex-col">
          <DialogTitle className="sr-only">{t('invoices.editPage.pdfPreviewTitle')} - {invoice.invoiceNumber}</DialogTitle>
          <DialogDescription className="sr-only">
            {t('invoices.editPage.pdfPreviewTitle')} {invoice.invoiceNumber}
          </DialogDescription>
          <div className="px-6 py-3 border-b flex flex-row items-center justify-between shrink-0">
            <h2 className="text-lg font-semibold">{t('invoices.editPage.pdfPreviewTitle')} - {invoice.invoiceNumber}</h2>
            <Button onClick={handleClosePdfPreview} variant="outline" size="sm">
              {t('invoices.editPage.closePreview')}
            </Button>
          </div>
          <div className="flex-1 overflow-hidden p-4">
            {pdfUrl && (
              <iframe
                src={pdfUrl}
                className="w-full h-full border-0"
                title={t('invoices.editPage.pdfPreviewTitle')}
              />
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('invoices.editPage.deleteTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {invoice.status === "draft" 
                ? t('invoices.editPage.deleteConfirmDraft')
                : t('invoices.editPage.deleteConfirmArchive')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                handleDelete();
              }}
              className="bg-red-600 hover:bg-red-700"
            >
              {t('invoices.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Cancel Invoice Dialog */}
      <AlertDialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('invoices.editPage.cancelTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('invoices.editPage.cancelDescription')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="py-4">
            <Label htmlFor="cancellation-reason">{t('invoices.editPage.cancellationReason')}</Label>
            <Textarea
              id="cancellation-reason"
              placeholder={t('invoices.editPage.cancellationReasonPlaceholder')}
              value={cancellationReason}
              onChange={(e) => setCancellationReason(e.target.value)}
              className="mt-2"
              rows={3}
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setCancellationReason("")}>
              {t('common.cancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleCancel}
              className="bg-orange-600 hover:bg-orange-700"
            >
              {t('invoices.editPage.cancel')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
